/**
 * Shared builder: PL `PersonalisedProductsWidgetData` (the personalised homepage swimlane) →
 * domain `CustomSection`.
 *
 * Mirrors `featured-products-showcase-section-builders.ts`: dependency-light, no JSX, so the widget
 * can render the existing `PersonalisedProducts` section chrome (DS + legacy routing + product-card
 * analytics) without duplicating the proto→domain conversion.
 *
 * Unlike the Contentful-driven flow, the feed is resolved at PL serve time (productFeedType /
 * productFeedLimit → RECOMMENDATION datasource) and arrives enriched as PRODUCT_CARD children —
 * so `fetchProductsFromAPI` is pinned false and the section never re-fetches client-side.
 */

import type { IProductCard } from '@src/components/product-card/product-card.types'
import CustomSection from '@src/quince-site-common/src/domains/CustomSection'
import CUSTOM_SECTION_TYPE from '@src/quince-site-common/src/domains/CustomSectionType'

import { makeSection } from '../banner-section-builders'
import { buildWidgetTrackingMetaData, buildWidgetTrackingTypes, protoButtonToDomain, toRichText } from '../widget-utils'

import type { PersonalisedProductsWidgetData } from '@lastbrand/storefront-proto-registry/api/v1/widget'

export function buildPersonalisedProductsConfig(
  data: PersonalisedProductsWidgetData,
  productCards: IProductCard[],
  tracker?: string,
  sectionPosition?: number,
): CustomSection {
  const heading = toRichText(data.heading as Parameters<typeof toRichText>[0])
  return makeSection({
    id: data.id,
    sectionType: data.sectionType as CUSTOM_SECTION_TYPE,
    richTexts: (heading ? [heading] : []) as CustomSection['richTexts'],
    buttons: data.buttons.map(protoButtonToDomain) as CustomSection['buttons'],
    productCards,
    // The PL feed is server-enriched; the section's client-side feed fetch must stay off.
    fetchProductsFromAPI: false,
    numberOfProductsToShowInDesktop: data.numberOfProductsToShowInDesktop,
    numberOfProductsToShowInMobile: data.numberOfProductsToShowInMobile,
    trackingMetaData: buildWidgetTrackingMetaData(data.analytics, sectionPosition),
    // Arms the PL section-level IS_VISIBLE/CLICK emitter (PLSectionTrackingWrapper) once the wire
    // ships trackingTypes — legacy sends ['VISIBLE'] for every home section (proto 3.20.0).
    trackingTypes: buildWidgetTrackingTypes(data.analytics),
    classNames: data.classNames,
    classNamesMobileOverrides: data.classNamesMobileOverrides,
    baseDynamicClasses: data.baseDynamicClasses,
    mobileDynamicClassesOverrides: data.mobileDynamicClassesOverrides,
    // Mirrors non-PL `SectionRenderer.tsx`'s unconditional `tracker: params.tracker` injection —
    // without it, every click/impression tracker string on this widget bakes in the literal
    // "undefined" (see STRFRNT home-PL analytics parity fix).
    tracker,
    // Derived 1-based page position — legacy bakes it into the section payload as both
    // `elementPosition` and `context.contentContainerPosition` (the PL wire carries neither).
    elementPosition: sectionPosition,
  })
}
